import { ChangeEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import DocumentItem from './components/DocumentItem.js';
import { TDocument } from '../../types.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { setIdentificationError } from '~/store/identification-form/slices.ts';
import { errorMessages } from '~/constants/errorMessages.js';
import { getFileInputId } from '../../utils.ts';
import { Box, Skeleton, Table, TableBody, Typography } from '@mui/material';
import { styles } from '../../styles.ts';

interface IDocumetnSection {
  headerText: string;
  documents: TDocument[];
  isEditable?: boolean;
  attachDocument?: (blockId: number, doc: TDocument, files: File[]) => void;
  setShowDeleteModal?: (isShown: boolean) => void;
  setCurrentDocId?: ({ idDoc, blockId }: { idDoc: number; blockId: number }) => void;
  fetchPreview?: (id: number | string) => void;
  blockId: number;
  showSvgEyeOpen?: boolean;
  showSection?: boolean;
  loadingDocId?: { blockId: number; index: number } | null;
}

const DocumentsSection = ({
  headerText,
  documents,
  isEditable,
  attachDocument,
  setShowDeleteModal,
  setCurrentDocId,
  fetchPreview,
  blockId,
  showSvgEyeOpen = true,
  showSection = true,
  loadingDocId,
}: IDocumetnSection) => {
  const dispatch = useAppDispatch();

  const handleDelete = (doc: TDocument) => {
    setCurrentDocId?.({ idDoc: doc.idDoc, blockId });
    setShowDeleteModal?.(true);
  };

  const handleAddDoc = (doc: TDocument) => {
    if (doc.idDoc) {
      setCurrentDocId?.({ idDoc: doc.idDoc, blockId });
      setShowDeleteModal?.(true);
    } else {
      document?.getElementById(getFileInputId(doc, blockId))?.click();
    }
  };

  const handleAttachDocument =
    (e: ChangeEvent<HTMLInputElement>) => (doc: TDocument, blockId: number) => {
      const files = Array.from(e.target.files || []);

      if (!files.length) return;
      if (files.length > 2) {
        dispatch(setIdentificationError({ errorMessage: errorMessages.ONLY_TWO_FILES_WITH_SIGN }));
        return;
      }
      attachDocument?.(blockId, doc, files);
    };
  if (!showSection) return;

  return (
    <Box key={blockId + uuidv4()} sx={styles.container}>
      <Typography variant="h5">{headerText}</Typography>
      {documents.length ? (
        <Table sx={styles.table}>
          <TableBody sx={styles.tableBody} key={blockId + uuidv4()}>
            {documents.map((document, index) => (
              <DocumentItem
                document={document}
                blockId={blockId}
                key={uuidv4()}
                isEditable={isEditable}
                showSvgEyeOpen={showSvgEyeOpen}
                handleDelete={handleDelete}
                handlePreview={fetchPreview}
                handleEdit={handleAddDoc}
                handleAttachDocument={handleAttachDocument}
                isLoading={
                  loadingDocId?.blockId === blockId &&
                  loadingDocId?.index === index &&
                  !document.idDoc
                }
              />
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box sx={{ width: '100%' }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton sx={{ height: 40 }} key={index} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DocumentsSection;
